import { z } from 'zod';

import { contentAvailableFromApiFields, parseAvailabilityBlockReason } from './utils';

export const TREE_ROOT_NODE_ID = 'root';

const treeNodeFolderEventSchema = z
  .object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullable(),
    start_at: z.string(),
    status: z.enum(['draft', 'published']),
  })
  .transform((raw) => {
    return {
      id: raw.id,
      name: raw.title,
      description: raw.description,
      createAt: raw.start_at,
      status: raw.status,
    };
  });

export const treeNodeFolderSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.literal('folder'),
    has_children: z.boolean(),
    event: treeNodeFolderEventSchema.nullable(),
    is_personal: z.boolean().optional().default(false),
    content_available: z.boolean().optional(),
    is_frozen: z.boolean().optional(),
    availability_block_reason: z.string().nullable().optional(),
  })
  .transform((raw) => {
    const reason = parseAvailabilityBlockReason(raw.availability_block_reason);

    return {
      id: raw.id,
      name: raw.name,
      type: raw.type,
      hasChildren: raw.has_children,
      isPersonal: raw.is_personal ?? false,
      contentAvailable: contentAvailableFromApiFields(raw),
      availabilityBlockReason: reason,
      ...(raw.event ? { event: raw.event } : {}),
    };
  });

export const treeNodeFileSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.literal('file'),
    file_name: z.string().nullable().optional(),
    content_type: z.string().nullable(),
    group_type: z.string().nullable(),
    dsb_status: z.string().nullable().optional(),
    content_available: z.boolean().optional(),
  })
  .transform((raw) => ({
    id: raw.id,
    name: raw.name,
    type: raw.type,
    fileName: raw.file_name ?? null,
    contentType: raw.content_type ?? '',
    groupType: raw.group_type,
    hasChildren: false,
    dsbStatus: raw.dsb_status ?? null,
    contentAvailable: contentAvailableFromApiFields(raw),
  }));

export const treeNodeSchema = z.discriminatedUnion('type', [treeNodeFolderSchema, treeNodeFileSchema]);

export type TreeNodeApi = z.input<typeof treeNodeSchema>;

export type TreeNodeFolderApi = z.input<typeof treeNodeFolderSchema>;
export type TreeNodeFolder = z.infer<typeof treeNodeFolderSchema>;
export type TreeNodeFileApi = z.input<typeof treeNodeFileSchema>;
export type TreeNodeFile = z.infer<typeof treeNodeFileSchema>;

export type TreeNode = TreeNodeFolder | TreeNodeFile;
